import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { adminAPI } from "../../services/api";
import { Search, CheckCircle, Circle, Download, ArrowLeft } from "lucide-react";
import "./Admin.css";

const AttendanceManager = () => {
  const { eventId } = useParams();
  const [masterclasses, setMasterclasses] = useState([]);
  const [selectedMc, setSelectedMc] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadMasterclasses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getMasterclasses(eventId);
      setMasterclasses(res.data);
    } catch (err) {
      console.error("Failed to load masterclasses", err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadMasterclasses();
  }, [loadMasterclasses]);

  const loadAttendees = async (mc) => {
    setLoading(true);
    setSelectedMc(mc);
    try {
      const res = await adminAPI.getAttendees(mc.id);
      setAttendees(res.data);
    } catch (err) {
      console.error("Failed to load attendees", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = async (userId, currentAttended) => {
    // Optimistic UI
    setAttendees((prev) =>
      prev.map((a) =>
        a.user_id === userId ? { ...a, attended: !currentAttended } : a,
      ),
    );

    try {
      await adminAPI.updateAttendance(selectedMc.id, userId, !currentAttended);
    } catch (err) {
      console.error("Failed to update attendance", err);
      // Revert on failure
      setAttendees((prev) =>
        prev.map((a) =>
          a.user_id === userId ? { ...a, attended: currentAttended } : a,
        ),
      );
    }
  };

  const filteredAttendees = attendees.filter(
    (a) =>
      a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (selectedMc) {
    return (
      <div className="admin-page">
        <div className="admin-section-header" style={{ marginBottom: "24px" }}>
          <button
            className="btn-admin btn-admin-outline"
            onClick={() => setSelectedMc(null)}
            style={{ marginBottom: "16px" }}
          >
            <ArrowLeft size={16} /> Back to Masterclasses
          </button>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h1 style={{ fontSize: "1.5rem", fontWeight: "700" }}>
                Attendance: {selectedMc.title}
              </h1>
              <p style={{ color: "var(--admin-text-sub)" }}>
                Check-in registered attendees
              </p>
            </div>
            <button className="btn-admin btn-admin-outline">
              <Download size={18} /> Export List
            </button>
          </div>
        </div>

        <div className="admin-card">
          <div className="card-header">
            <div
              className="admin-search-bar"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "#f1f5f9",
                padding: "8px 16px",
                borderRadius: "8px",
                width: "300px",
              }}
            >
              <Search size={16} color="#64748b" />
              <input
                type="text"
                placeholder="Search attendee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  width: "100%",
                  fontSize: "0.875rem",
                }}
              />
            </div>
          </div>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Attendee</th>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredAttendees.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      No attendees found.
                    </td>
                  </tr>
                ) : (
                  filteredAttendees.map((att) => (
                    <tr key={att.user_id}>
                      <td>
                        <div style={{ fontWeight: "600" }}>{att.name}</div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {att.email}
                        </div>
                      </td>
                      <td>{att.organization}</td>
                      <td>
                        <span
                          className={`admin-badge ${att.status === "registered" ? "badge-available" : "badge-limited"}`}
                        >
                          {att.status}
                        </span>
                      </td>
                      <td>
                        <button
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: att.attended
                              ? "var(--admin-success)"
                              : "#94a3b8",
                          }}
                          onClick={() =>
                            toggleAttendance(att.user_id, att.attended)
                          }
                        >
                          {att.attended ? (
                            <CheckCircle size={24} />
                          ) : (
                            <Circle size={24} />
                          )}
                          <span style={{ fontWeight: "600" }}>
                            {att.attended ? "Present" : "Check-in"}
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-section-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: "700" }}>
          Attendance Management
        </h1>
        <p style={{ color: "var(--admin-text-sub)" }}>
          Select a masterclass to manage attendance
        </p>
      </div>

      <div
        className="admin-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "20px",
        }}
      >
        {loading ? (
          <div
            style={{ padding: "40px", textAlign: "center", gridColumn: "1/-1" }}
          >
            Loading...
          </div>
        ) : (
          masterclasses.map((mc) => (
            <div
              key={mc.id}
              className="admin-card"
              style={{ cursor: "pointer", transition: "transform 0.2s" }}
              onClick={() => loadAttendees(mc)}
            >
              <div style={{ padding: "24px" }}>
                <h3 style={{ margin: "0 0 8px 0", fontSize: "1.1rem" }}>
                  {mc.title}
                </h3>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="admin-badge badge-available">
                    {mc.current_registrations} Attendees
                  </span>
                  <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {new Date(mc.start_time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
              <div
                style={{
                  padding: "12px 24px",
                  background: "#f8fafc",
                  borderTop: "1px solid var(--admin-border)",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "var(--admin-primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Manage Attendance →
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AttendanceManager;
